import { Container, Heading, Text, Box, Flex, keyframes, useBreakpointValue } from '@chakra-ui/react';
import { ButtonGroup } from 'components/common/ButtonGroup';
import Image from 'next/image';

export default function FloatingLogosSection({ size, title, description, buttons, logos }) {
  
  const isMobile = useBreakpointValue({ base: true, md: false });

  const bubbleSizesMobile = [
    '32px',
    '32px',
    '49px',
    '43px',
    '41px',
    '33px',
    '32px',
    '45px',
    '40px',
    '32px',
    '59px',
    '41px',
  ];

  const MobileLogo = ({ logo, index }) => {
    const wave = keyframes`
      from { transform: translateY(${logo.animationTranslateY || 10}px); }
      to   { transform: translateY(0); }
    `;
    const bubbleSize = bubbleSizesMobile[index] || '60px';
    const bubbleNumeric = parseInt(bubbleSize, 10);
    const computedIconSize = `${Math.floor((bubbleNumeric * 2) / 3)}px`;

    return (
      <Box
        position="relative"
        width={bubbleSize}
        height={bubbleSize}
        bg="white"
        boxShadow="lg"
        borderRadius="full"
        overflow="hidden"
        animation={`${wave} 3s ease-in-out infinite alternate`}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Box position="relative" width={computedIconSize} height={computedIconSize}>
          <Image
            src={logo.image.filename}
            alt={logo.image.alt}
            layout="fill"
            objectFit="contain"
          />
        </Box>
      </Box>
    );
  };

  const DesktopLogo = ({ logo }) => {
    const wave = keyframes`
      from { transform: translateY(${logo.animationTranslateY}px); }
      to   { transform: translateY(0); }
    `;
    return (
      <Box
        position="absolute"
        bg="white"
        boxShadow="lg"
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={logo.size}
        height={logo.size}
        borderRadius="full"
        top={logo.top}
        left={logo.left}
        animation={`${wave} 3s ease-in-out infinite alternate`}
      >
        <Image
          src={logo.image.filename}
          alt={logo.image.alt}
          width={logo.imageWidth}
          height={logo.imageHeight}
        />
      </Box>
    );
  };

  return (
    <Container as="section" size={size} maxW="full" px={0}>
      {title && (
        <Box
          textAlign="center"
          maxWidth="1024px"
          mx="auto"
          mb={{ base: '40px', desktop: '80px' }}
          px={{ base: '20px', md: '0' }}
        >
          <Heading as="h2" size="lg" mb="16px">
            {title}
          </Heading>
          {description && <Text>{description}</Text>}
          <ButtonGroup buttons={buttons} justifyContent="center" mt="34px" />
        </Box>
      )}
      <Box
        position="relative"
        w="full"
        h={{ base: '250px', md: '425px' }}
        backgroundImage="url('/assets/waves.svg')"
        backgroundPosition={{ base: 'center', md: 'center bottom' }}
        backgroundRepeat="no-repeat"
        backgroundSize={{ base: '150%', md: 'auto' }}
        overflow="hidden"
      >
        {isMobile ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            height="100%"
            gap={2}
          >
            <Flex width="50%" justify="space-evenly" align="center">
              {logos.slice(0, 2).map((logo, i) => (
                <MobileLogo key={i} logo={logo} index={i} />
              ))}
            </Flex>
            <Flex width="80%" justify="space-evenly" align="center">
              {logos.slice(2, 5).map((logo, i) => (
                <MobileLogo key={i + 2} logo={logo} index={i + 2} />
              ))}
            </Flex>
            <Flex width="100%" justify="space-evenly" align="center">
              {logos.slice(5, 9).map((logo, i) => (
                <MobileLogo key={i + 5} logo={logo} index={i + 5} />
              ))}
            </Flex>
            <Flex width="100%" justify="space-evenly" align="center">
              {logos.slice(9, 12).map((logo, i) => (
                <MobileLogo key={i + 9} logo={logo} index={i + 9} />
              ))}
            </Flex>
          </Flex>
        ) : (
          <Flex position="relative" width="20px" height="100%" mx="auto">
            {logos.map((logo, i) => (
              <DesktopLogo key={i} logo={logo} />
            ))}
          </Flex>
        )}
      </Box>
    </Container>
  );
}
